import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Image, message, Select, Space, Spin, Tabs } from "antd";
import { generatePath, useHistory } from "react-router-dom";
import TradeModal from "../../../components/TradeModal";
import ActionButtonGroups from "../../../components/ActionButtonGroups";
import { getUserInfo, generateNewRelicLink } from "../../../utils";
import Service from "../../../services";
import styles from "./index.module.scss";
import shareImg from "../../../assets/images/share.svg";
import CustomChart from "../../../components/accountDetails/CustomChart";
import AccountBalances from "../../../components/accountDetails/AccountBalances";
import { filterTradeStatusList } from "../../../components/accountOverview/GroupTable";
import TransferButton from "../../../components/accountDetails/TransferButton";
import ExternalAccountSwitch from "../../../components/accountDetails/ExternalAccountSwitch";
import AlgoTab from "../../../components/accountDetails/AlgoTab";
import { naturalSortSessions } from "../../../utils";

const { Option } = Select;

const AccountDetails = ({
  match: {
    params: { instrumentSymbol },
  },
}) => {
  const history = useHistory();

  const [userInfo, setUserInfo] = useState();
  const [accountList, setAccountList] = useState([]); // 有哪些account可以选择
  const [accountIndex, setAccountIndex] = useState(); // 选了哪个account
  const [tradeAccountStatus, setTradeAccountStatus] = useState();

  const [mainLoading, setMainLoading] = useState(true);
  const [balances, setBalances] = useState(); // 图标Y轴数值数据
  const customChartRef = useRef();
  const [otherSiblingAccountStatuses, setOtherSiblingAccountStatuses] = useState();

  useEffect(() => {
    setUser();
    addCssStyleToBabBarExtraContent();
  }, []);

  useEffect(() => {
    if (userInfo) {
      const getAccountList = async () => {
        try {
          const listManagedTradeAccountStatus = await Service.listManagedTradeAccountStatus();
          const filteredAccounts = listManagedTradeAccountStatus
            .map(({ parsedTradeAccountStatusList }) => filterTradeStatusList(parsedTradeAccountStatusList))
            .flat()
            .filter((account) => account.permissions.includes("READ") && !account.exchange.toLowerCase().endsWith("spot"))
            .sort((a, b) => {
              const parentA = a?.parentAccount || "";
              const parentB = b?.parentAccount || "";
              if (parentA !== parentB) {
                return parentA.localeCompare(parentB);
              }
              return naturalSortSessions(a.account, b.account);
            });
          setAccountList(filteredAccounts);

          const accountIndex = filteredAccounts.findIndex((account) => {
            return account.key === instrumentSymbol;
          });
          if (accountIndex >= 0) {
            setAccountIndex(accountIndex);
          } else if (filteredAccounts.length === 0) {
            setAccountIndex(-1);
          } else {
            console.log(`${instrumentSymbol} does not match any account. Redirecting to default - ${filteredAccounts[0].key}`);
            const defaultPath = generatePath("/main/accountDetails/:instrumentSymbol", {
              instrumentSymbol: filteredAccounts[0].key,
            });
            setAccountIndex(0);
            history.push(defaultPath);
          }
        } catch (error) {
          message.error(String(error));
        }
      };
      getAccountList();
    }
  }, [history, instrumentSymbol, userInfo]);

  useEffect(() => {
    const fetchTradeAccountStatus = async () => {
      try {
        const parsedAccount = accountList[accountIndex];
        const tradeAccountStatus = await Service.getAndParseTradeAccountStatus(parsedAccount.exchange, parsedAccount.accountSymbol);
        // add permissions to tradeAccountStatus to avoid showing trade button for read-only accounts
        tradeAccountStatus.permissions = [...parsedAccount.permissions];
        setTradeAccountStatus(tradeAccountStatus);
        const siblings = accountList.filter(
          (account) =>
            account.exchange === parsedAccount.exchange &&
            account.exchangeAccount !== parsedAccount.exchangeAccount &&
            (account.parentAccount === parsedAccount.parentAccount || account.account === parsedAccount.parentAccount)
        );
        setOtherSiblingAccountStatuses(siblings);
      } catch (error) {
        message.error(String(error));
      }
    };

    if (accountIndex >= 0) {
      fetchTradeAccountStatus();
      customChartRef.current?.fetchAccountBalances();
    }
  }, [accountIndex, accountList]);

  const setUser = async () => {
    try {
      setUserInfo(await getUserInfo());
    } catch (error) {
      message.error(String(error));
    }
  };

  // Ant Design does not provide a direct prop to style tabBarExtraContent in the Tabs component, so we have to add CSS to dynamically
  const addCssStyleToBabBarExtraContent = () => {
    const style = document.createElement("style");
    style.type = "text/css";
    style.innerHTML = `
      .ant-tabs-nav .ant-tabs-extra-content {
        order: -1;
        width: 90%;
      }
    `;
    document.head.appendChild(style);
  };

  const showTradeModal = tradeAccountStatus && tradeAccountStatus.actionType;

  if (accountIndex === -1) {
    return <Alert message="You do not have access to any trading accounts" type="info" />;
  }

  const header = (
    <div className={styles.header}>
      <div className={styles.title}>Account Details</div>
      <div className={styles.text}>Account：</div>
      <Select
        showSearch
        style={{ width: 480 }}
        value={accountIndex}
        onChange={(i) => {
          setMainLoading(true);
          customChartRef.current?.setCurrencyTypeList([]);
          setAccountIndex(i);
        }}
        optionFilterProp="children"
      >
        {accountList.map((e, i) => (
          <Option key={i} value={i}>
            {`${e.exchange} ${e.account} (${e.symbol})`}
          </Option>
        ))}
      </Select>
      <Button
        className={styles.shareBtn}
        type="link"
        href={
          tradeAccountStatus && generateNewRelicLink(tradeAccountStatus.exchange, tradeAccountStatus.account, tradeAccountStatus.symbol)
        }
        target="_blank"
      >
        <Image width={17} height={17} preview={false} src={shareImg}></Image>
        Trade Logs
      </Button>
      {tradeAccountStatus?.permissions?.includes("CONTROL") && (
        <>
          <TransferButton exchangeId={accountList[accountIndex]?.exchangeId} account={accountList[accountIndex]?.account} />
        </>
      )}
    </div>
  );

  const getSpotAlgoTab = (tradeAccountStatus) => <AlgoTab futureTradeAccountStatus={tradeAccountStatus} algoInstrumentType="spot_algo" />;
  const getFutureAlgoTab = (tradeAccountStatus) => (
    <AlgoTab futureTradeAccountStatus={tradeAccountStatus} algoInstrumentType="future_algo" />
  );

  const tabs = [
    {
      label: "Future",
      key: "future",
      children: (
        <Spin spinning={mainLoading} tip="Loading...">
          <div className={styles.detail}>
            <div className={styles.detailHeader}>
              <div className={styles.title}>Trade Details</div>
              <Space className={styles.btnGroup}>
                {tradeAccountStatus?.permissions?.includes("CONTROL") && (
                  <>
                    <ExternalAccountSwitch tradeAccountStatus={tradeAccountStatus} setTradeAccountStatus={setTradeAccountStatus} />
                    <ActionButtonGroups actionDetails={tradeAccountStatus} setActionDetails={setTradeAccountStatus} />
                  </>
                )}
              </Space>
            </div>
            <div className={styles.numList}>
              <div className={styles.numRow}>
                <div className={styles.numLi}>
                  <div className={styles.numName}>Exchange</div>
                  <div className={styles.numText}>{tradeAccountStatus?.exchange}</div>
                </div>
                <div className={styles.numLi} style={{ flex: "0 0 400px" }}>
                  <div className={styles.numName}>Account</div>
                  <div className={styles.numText}>{tradeAccountStatus && accountList[accountIndex].account}</div>
                </div>
                <div className={styles.numLi}>
                  <div className={styles.numName}>Trading pair</div>
                  <div className={styles.numText}>{tradeAccountStatus?.symbol}</div>
                </div>
                <div className={styles.numLi}>
                  <div className={styles.numName}>Leverage</div>
                  <div className={styles.numText}>{tradeAccountStatus?.leverage || "-"}</div>
                </div>
              </div>
              <div className={styles.numRow}>
                <div className={styles.numLi}>
                  <div className={styles.numName}>Balance</div>
                  <div className={styles.numText}>${tradeAccountStatus?.marginBalance || "-"}</div>
                </div>
                <div className={styles.numLi} style={{ flex: "0 0 400px" }}>
                  <div className={styles.numName}>Balance Allocation Ratio</div>
                  <div className={styles.numText}>{tradeAccountStatus?.parsedConfigs?.balanceAllocationRatio || "-"}%</div>
                </div>
                <div className={styles.numLi}>
                  <div className={styles.numName}>Status</div>
                  <div className={styles.numText}>{tradeAccountStatus?.status || "-"}</div>
                </div>
                <div className={styles.numLi}>
                  <div className={styles.numName}>Position </div>
                  <div className={styles.numText}>{tradeAccountStatus?.currentState || "-"}</div>
                </div>
              </div>
            </div>
            <CustomChart
              accountList={accountList}
              accountIndex={accountIndex}
              customChartRef={customChartRef}
              setMainLoading={setMainLoading}
              setBalance={setBalances}
              tradingPair={tradeAccountStatus?.symbol}
            ></CustomChart>
          </div>
          <div className={styles.balances}>
            <AccountBalances balances={balances}></AccountBalances>
          </div>
          {showTradeModal && (
            <TradeModal
              tradeModelDetails={tradeAccountStatus}
              siblingAccountStatuses={otherSiblingAccountStatuses}
              username={userInfo.username}
              closeModal={() => {
                setTradeAccountStatus({ ...tradeAccountStatus, actionType: undefined });
              }}
            />
          )}
        </Spin>
      ),
    },
  ];

  if (tradeAccountStatus?.permissions?.includes("CONTROL")) {
    tabs.push({
      label: "Spot Algo",
      key: "spotAlgo",
      children: getSpotAlgoTab(tradeAccountStatus),
    });
    tabs.push({
      label: "Future Algo",
      key: "futureAlgo",
      children: getFutureAlgoTab(tradeAccountStatus),
    });
  }

  return (
    <div className={styles.accountDetails}>
      <Tabs tabBarExtraContent={header} tabPosition="top" items={tabs} defaultActiveKey="future" />
    </div>
  );
};
export default AccountDetails;
